/**
 * A small typed event emitter.
 *
 * `Events` maps event names to payload types, so `emit` and `on` are checked
 * at compile time. Dispatch iterates over a snapshot of the listeners, so a
 * listener may unsubscribe itself (or others) while an event is in flight.
 */

export type Unsubscribe = () => void;

export class Emitter<Events extends object> {
  private readonly listeners = new Map<keyof Events, Set<(payload: never) => void>>();

  on<K extends keyof Events>(type: K, listener: (payload: Events[K]) => void): Unsubscribe {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener);

    return () => {
      set.delete(listener);
    };
  }

  emit<K extends keyof Events>(type: K, payload: Events[K]): void {
    const set = this.listeners.get(type);
    if (!set) return;

    for (const listener of [...set]) {
      (listener as (payload: Events[K]) => void)(payload);
    }
  }
}
